import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { Button, Select, TextInput } from 'components/Form'
import { getCompanyData, getCustomersData } from 'store/data/dataSlice'

// Validation schema for the form
const getValidationSchema = (integrationType, isEdit = false) => {
    const baseSchema = {
        shop_name: Yup.string().required('Shop name field is required'),
        company_id: Yup.string().required('Company field is required'),
        customer_id: Yup.string().required('Customer field is required'),
        integration_type: Yup.string().required('Integration type is required'),
    }

    if (integrationType === 'custom_app') {
        baseSchema.api_key = Yup.string().required('API key is required for custom app integration')
        baseSchema.api_secret = Yup.string().required('API secret is required for custom app integration')

        // In edit mode, access token is optional (blank means keep current)
        if (isEdit) {
            baseSchema.access_token = Yup.string()
        } else {
            baseSchema.access_token = Yup.string().required('Admin API access token is required for custom app integration')
        }
    }

    return Yup.object().shape(baseSchema)
}

const ShopifyForm = ({ initialValues, onSubmit, onCancel, isEdit = false }) => {
    const dispatch = useDispatch()
    // Access company and customer data from the Redux store's listData slice
    const { companyData, customersData } = useSelector(state => state.listData || {})

    // Set default integration type if not provided
    const [integrationType, setIntegrationType] = useState(initialValues?.integration_type || 'one_click')

    useEffect(() => {
        dispatch(getCompanyData())
        dispatch(getCustomersData())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Update validation schema when integration type changes
    useEffect(() => {
        // This will trigger form re-validation when integration type changes
    }, [integrationType])

    // Process initial values for edit mode
    const processedInitialValues = useMemo(() => {
        const baseValues = {
            integration_type: 'one_click',
            ...initialValues,
        }

        // In edit mode, clear the access_token field for security while keeping access_token_masked for display
        if (isEdit && baseValues.access_token) {
            baseValues.access_token = ''
        }

        return baseValues
    }, [initialValues, isEdit])

    return (
        <Formik
            initialValues={processedInitialValues}
            validationSchema={getValidationSchema(integrationType, isEdit)}
            onSubmit={onSubmit}
            enableReinitialize={true}
        >
            {({ errors, touched, values, setFieldValue }) => {
                // Update state when integration type changes
                if (values.integration_type !== integrationType) {
                    setIntegrationType(values.integration_type)
                }

                return (
                    <Form>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                            <Field name="integration_type">
                                {({ field, form }) => (
                                    <Select
                                        className="md:col-span-4"
                                        label="Integration Type"
                                        name="integration_type"
                                        error={errors.integration_type && touched.integration_type}
                                        message={errors.integration_type}
                                        value={values?.integration_type}
                                        onChange={e => {
                                            form.setFieldValue(field.name, e.target.value)
                                            setIntegrationType(e.target.value)
                                        }}
                                        placeholder="Select Integration Type"
                                        options={[
                                            { label: 'One-Click Auth (Recommended)', value: 'one_click' },
                                            { label: 'Custom App', value: 'custom_app' },
                                        ]}
                                    />
                                )}
                            </Field>
                            <Field name="company_id">
                                {({ field, form }) => (
                                    <Select
                                        className="md:col-span-2"
                                        label="Company"
                                        name="company_id"
                                        error={errors.company_id && touched.company_id}
                                        message={errors.company_id}
                                        value={values?.company_id}
                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                        placeholder="Select Company"
                                        options={
                                            companyData && companyData.length > 0
                                                ? companyData.map(item => ({
                                                      label: item.label,
                                                      value: item.id,
                                                  }))
                                                : []
                                        }
                                        disabled
                                    />
                                )}
                            </Field>
                            <Field name="customer_id">
                                {({ field, form }) => (
                                    <Select
                                        className="md:col-span-2"
                                        label="Customer"
                                        name="customer_id"
                                        error={errors.customer_id && touched.customer_id}
                                        message={errors.customer_id}
                                        value={values?.customer_id}
                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                        placeholder="Select Customer"
                                        options={
                                            customersData && customersData.length > 0
                                                ? customersData.map(item => ({
                                                      label: item.label,
                                                      value: item.id,
                                                  }))
                                                : []
                                        }
                                        disabled
                                    />
                                )}
                            </Field>
                            <Field name="shop_name">
                                {({ field, form }) => (
                                    <>
                                        <TextInput
                                            type="text"
                                            className="md:col-span-2"
                                            label="Shop Name"
                                            name="shop_name"
                                            error={errors.shop_name && touched.shop_name}
                                            message={errors.shop_name}
                                            subText="Enter your Shopify store name (e.g., 'your-store' without .myshopify.com)"
                                            value={values?.shop_name}
                                            onChange={e => form.setFieldValue(field.name, e.target.value)}
                                        />
                                    </>
                                )}
                            </Field>

                            {/* Custom App Fields - only show when custom_app is selected */}
                            {values.integration_type === 'custom_app' && (
                                <>
                                    <Field name="api_key">
                                        {({ field, form }) => (
                                            <TextInput
                                                type="text"
                                                className="md:col-span-2"
                                                label="API Key"
                                                name="api_key"
                                                error={errors.api_key && touched.api_key}
                                                message={errors.api_key}
                                                subText="Enter your Custom App API Key from Shopify Admin"
                                                value={values?.api_key}
                                                onChange={e => form.setFieldValue(field.name, e.target.value)}
                                            />
                                        )}
                                    </Field>
                                    <Field name="api_secret">
                                        {({ field, form }) => (
                                            <TextInput
                                                type="password"
                                                className="md:col-span-2"
                                                label="API Secret"
                                                name="api_secret"
                                                error={errors.api_secret && touched.api_secret}
                                                message={errors.api_secret}
                                                subText="Enter your Custom App API Secret from Shopify Admin"
                                                value={values?.api_secret}
                                                onChange={e => form.setFieldValue(field.name, e.target.value)}
                                            />
                                        )}
                                    </Field>
                                    <Field name="access_token">
                                        {({ field, form }) => (
                                            <TextInput
                                                type="password"
                                                className="md:col-span-4"
                                                label="Admin API Access Token"
                                                name="access_token"
                                                error={errors.access_token && touched.access_token}
                                                message={errors.access_token}
                                                subText={
                                                    isEdit && values?.access_token_masked
                                                        ? `Current token: ${values.access_token_masked} (leave blank to keep current token)`
                                                        : 'Enter your Custom App Admin API Access Token from Shopify Admin'
                                                }
                                                placeholder={isEdit ? 'Leave blank to keep current token' : 'Enter access token'}
                                                value={values?.access_token || ''}
                                                onChange={e => form.setFieldValue(field.name, e.target.value)}
                                            />
                                        )}
                                    </Field>
                                </>
                            )}

                            <div className="md:col-span-4">
                                <div className="p-4 mb-4 text-sm text-blue-800 rounded-lg bg-blue-50 dark:bg-gray-800 dark:text-blue-400">
                                    <p className="font-medium">
                                        {values.integration_type === 'custom_app'
                                            ? 'Custom App Integration'
                                            : 'One-Click Integration'}
                                    </p>
                                    {values.integration_type === 'custom_app' ? (
                                        <>
                                            <p>Use your custom Shopify app credentials for integration.</p>
                                            <p className="mt-2">
                                                <strong>Step 1:</strong> Create a custom app in your Shopify Admin with the
                                                required scopes.
                                                <br />
                                                <strong>Step 2:</strong> Install the app to get the Admin API access token.
                                                <br />
                                                <strong>Step 3:</strong> Copy the API Key, API Secret, and Admin API Access Token
                                                here.
                                            </p>
                                            <p className="mt-2">
                                                <strong>Required Scopes:</strong> write_assigned_fulfillment_orders,
                                                read_assigned_fulfillment_orders, read_fulfillments, write_fulfillments,
                                                write_inventory, read_inventory, write_locations, read_locations, write_orders,
                                                read_orders, read_products, write_products, read_shipping, write_shipping,
                                                read_customers, write_merchant_managed_fulfillment_orders,
                                                read_merchant_managed_fulfillment_orders, write_third_party_fulfillment_orders,
                                                read_third_party_fulfillment_orders
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <p>
                                                API credentials are securely managed through environment variables on the server.
                                            </p>
                                            {isEdit ? (
                                                <p>You can update your shop details here.</p>
                                            ) : (
                                                <>
                                                    <p>You only need to provide your shop name and connect with one click.</p>
                                                    <p className="mt-2">
                                                        After clicking "Connect", you will be redirected to Shopify to authorize
                                                        the integration. Once authorized, you will be redirected back to the
                                                        integration page.
                                                    </p>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-4 mt-3">
                            <Button
                                className="h-10 w-18 text-primary border border-primary dark:text-secondary dark:border-secondary"
                                onClick={onCancel}
                            >
                                {isEdit ? 'Cancel' : 'Discard'}
                            </Button>
                            <Button
                                type="submit"
                                className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-primary bg-primary dark:border-secondary dark:bg-secondary"
                            >
                                {isEdit ? 'Update' : 'Connect'}
                            </Button>
                        </div>
                    </Form>
                )
            }}
        </Formik>
    )
}

export default ShopifyForm
